import HttpsRoundedIcon from '@mui/icons-material/HttpsRounded';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Card, Container, Link, Typography } from '@mui/material';
import { useFormik } from 'formik';
import LogRocket from 'logrocket';
import { useContext, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { object, ObjectSchema, string } from 'yup';
import AuthApi from '../../apis/AuthApi';
import FormikTextField from '../../components/FormikTextField/FormikTextField';
import SnackbarContext from '../../contexts/SnackbarContext';
import { UserLoginDto } from '../../generated-types/userLoginDto';
import SearchParamsLib from '../../lib/SearchParamsLib';
import useApi from '../../lib/useApi';
import useFooterControl from '../../lib/useFooterControl';
import useSharedAuthLib from '../../lib/useSharedAuthLib';

/**
 * A view for logging in the user
 */
const LoginView = () => {
  const authApi = useApi(AuthApi);
  const schema: ObjectSchema<UserLoginDto> = object({
    email: string().defined('Required').email('Email must be valid'),
    password: string().defined('Required'),
  });
  const snackbarContext = useContext(SnackbarContext);
  const authLib = useSharedAuthLib();
  const navigate = useNavigate();
  useFooterControl(true, false);

  const formik = useFormik<UserLoginDto>({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: schema,
    onSubmit: (val) => {
      // Record the user trying to login
      LogRocket.identify(val.email);

      // Log the user in
      authApi
        .Login(val)
        .then((response) => {
          switch (response.code) {
            case 200: {
              // Set the authentication - the response token will always be populated if we get a 201
              if (response.token) authLib.SetToken(response.token);

              break;
            }
            case 404:
              formik.setFieldError('email', 'Email not found.');
              formik.setSubmitting(false);
              break;
            case 401:
              formik.setFieldError('password', 'Incorrect password.');
              formik.setSubmitting(false);
              break;
            default:
              throw Error();
          }
        })
        .catch(() => {
          snackbarContext.setAlert(
            <Alert variant="filled" severity="error">
              An unrecognized error occurred while trying to log in. Please try
              again.
            </Alert>
          );
          snackbarContext.triggerSnackbar();
          formik.setSubmitting(false);
        });
    },
  });

  useEffect(() => {
    // Redirect the user once they have officially been authenticated,
    //  which includes processing of the new token
    if (authLib.AuthState.Authenticated) {
      const searchParams = new URLSearchParams(window.location.search);
      const returnUrl = searchParams.get(SearchParamsLib.ReturnUrl);

      if (returnUrl == null) {
        navigate(authLib.GetNavigateRouteBasedOnRole());
      } else {
        navigate(returnUrl);
      }
    }
  }, [authLib, authLib.AuthState.Authenticated, navigate]);

  return (
    <Box height="100%" width="100%">
      <Container
        maxWidth="sm"
        sx={{
          paddingY: '1em',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Card elevation={5} sx={{ padding: '2em', margin: 'auto' }}>
          <Typography variant="h1" fontSize="3em" gutterBottom>
            Welcome to S3G!
          </Typography>
          <Typography marginBottom="1em">
            Please log in using the form below.
          </Typography>
          <form onSubmit={formik.handleSubmit}>
            <FormikTextField
              id="login-email"
              formik={formik}
              fullWidth
              label="Email"
              name="email"
              autoFocus
              sx={{
                marginBottom: '1em',
              }}
            />
            <FormikTextField
              id="login-password"
              formik={formik}
              fullWidth
              label="Password"
              name="password"
              type="password"
              autoComplete="new-password"
              sx={{
                marginBottom: '1em',
              }}
            />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <LoadingButton
                id="login-button"
                loading={formik.isSubmitting}
                loadingPosition="end"
                type="submit"
                variant="contained"
                endIcon={<HttpsRoundedIcon />}
              >
                Log in
              </LoadingButton>
              <Link
                to="/forgot-password"
                component={RouterLink}
                color="textPrimary"
              >
                Forgot password?
              </Link>
            </Box>
          </form>
        </Card>
      </Container>
    </Box>
  );
};

export default LoginView;
